import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import api from '../../services/api';
import { toast } from 'sonner';
import { Star, Check, X, Calendar, User, Mail, Phone, BookOpen, AlertTriangle, Bell, ExternalLink, GraduationCap, Trash2, Archive, ArchiveRestore, Send, Loader2 } from 'lucide-react';
import TrialEnrollDialog from '../../components/admin/TrialEnrollDialog';

const TrialRequests = () => {
  const [trials, setTrials] = useState([]);
  const [classes, setClasses] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [selectedTrial, setSelectedTrial] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Cancel confirmation dialog state
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [trialToCancel, setTrialToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // Enroll-from-trial dialog state
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [trialToEnroll, setTrialToEnroll] = useState(null);

  // iter389 — bulk selection + actions (delete / archive / email) on Approved + Archived tabs
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: '', message: '' });

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const switchTab = (tab) => { clearSelection(); setActiveTab(tab); };
  
  const [approvalData, setApprovalData] = useState({
    program_id: '',
    class_id: '',
    trial_date: '',
    admin_notes: ''
  });

  const fetchData = async () => {
    try {
      const [trialsRes, classesRes, programsRes] = await Promise.all([
        api.get('/trial-requests'),
        api.get('/classes'),
        api.get('/programs')
      ]);
      setTrials(trialsRes.data);
      setClasses(classesRes.data);
      setPrograms(programsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openApprovalDialog = (trial) => {
    setSelectedTrial(trial);
    setApprovalData({
      program_id: '',
      class_id: trial.assigned_class_id || '',
      trial_date: trial.trial_date || '',
      admin_notes: trial.admin_notes || ''
    });
    setApprovalDialog(true);
  };

  const handleProgramChange = (programId) => {
    setApprovalData(prev => ({
      ...prev,
      program_id: programId,
      class_id: '' // Reset class when program changes
    }));
  };

  const getFilteredClasses = () => {
    if (!approvalData.program_id) return [];
    return classes.filter(c => c.program_id === approvalData.program_id);
  };

  const handleApprove = async () => {
    if (!approvalData.class_id || !approvalData.trial_date) {
      toast.error('Please select a class and trial date');
      return;
    }
    try {
      await api.put(`/trial-requests/${selectedTrial.id}/approve`, approvalData);
      toast.success('Trial request approved');
      setApprovalDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleComplete = async (trialId) => {
    try {
      await api.put(`/trial-requests/${trialId}/complete`);
      toast.success('Trial marked as completed');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openCancelConfirmation = (trial) => {
    setTrialToCancel(trial);
    setCancelConfirmOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!trialToCancel) return;
    
    setCancelling(true);
    try {
      await api.put(`/trial-requests/${trialToCancel.id}/cancel`);
      toast.success('Trial request cancelled');
      fetchData();
      setCancelConfirmOpen(false);
      setTrialToCancel(null);
    } catch (error) {
      toast.error('Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  const pendingTrials = trials.filter(t => t.status === 'pending' && !t.archived);
  const approvedTrials = trials.filter(t => t.status === 'approved' && !t.archived);
  const completedTrials = trials.filter(t => (t.status === 'completed' || t.status === 'cancelled' || t.status === 'enrolled') && !t.archived);
  const archivedTrials = trials.filter(t => t.archived);

  const selectAllIn = (list) => {
    const ids = list.map(t => t.id);
    const allSelected = ids.length > 0 && ids.every(id => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(ids));
  };

  const handleBulkArchive = async (archived) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      await api.post('/trial-requests/bulk-archive', { trial_ids: ids, archived });
      toast.success(`${ids.length} ${archived ? 'archived' : 'restored'}`);
      clearSelection();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post('/trial-requests/bulk-delete', { trial_ids: ids });
      toast.success(`${data.deleted} trial request(s) deleted`);
      clearSelection();
      setBulkDeleteOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkEmail = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!emailForm.subject.trim() || !emailForm.message.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post('/trial-requests/bulk-email', {
        trial_ids: ids,
        subject: emailForm.subject,
        message: emailForm.message,
      });
      toast.success(`Email sent to ${data.sent} parent(s)${data.failed ? ` · ${data.failed} failed` : ''}`);
      setBulkEmailOpen(false);
      setEmailForm({ subject: '', message: '' });
      clearSelection();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setBulkBusy(false);
    }
  };

  const BulkBar = ({ list }) => {
    const ids = list.map(t => t.id);
    const allSelected = ids.length > 0 && ids.every(id => selectedIds.has(id));
    const count = ids.filter(id => selectedIds.has(id)).length;
    return (
      <div className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded-lg border border-slate-200 bg-slate-50" data-testid="trial-bulk-bar">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
          <Checkbox
            checked={allSelected}
            onCheckedChange={() => selectAllIn(list)}
            data-testid="trial-select-all"
          />
          Select all
        </label>
        <span className="text-sm text-slate-500" data-testid="trial-selected-count">{count} selected</span>
        <div className="flex-1" />
        <Button size="sm" variant="outline" disabled={count === 0 || bulkBusy}
          onClick={() => setBulkEmailOpen(true)} data-testid="trial-bulk-email-btn">
          <Mail className="h-4 w-4 mr-1" /> Send Email
        </Button>
        {activeTab === 'archived' ? (
          <Button size="sm" variant="outline" disabled={count === 0 || bulkBusy}
            onClick={() => handleBulkArchive(false)} data-testid="trial-bulk-restore-btn">
            <ArchiveRestore className="h-4 w-4 mr-1" /> Restore
          </Button>
        ) : (
          <Button size="sm" variant="outline" disabled={count === 0 || bulkBusy}
            onClick={() => handleBulkArchive(true)} data-testid="trial-bulk-archive-btn">
            <Archive className="h-4 w-4 mr-1" /> Archive
          </Button>
        )}
        <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50"
          disabled={count === 0 || bulkBusy} onClick={() => setBulkDeleteOpen(true)} data-testid="trial-bulk-delete-btn">
          <Trash2 className="h-4 w-4 mr-1" /> Delete
        </Button>
      </div>
    );
  };

  const TrialCard = ({ trial, showActions = false, selectable = false }) => {
    const isGuest = trial.is_guest || trial.parent_id?.startsWith('guest_');
    const selected = selectedIds.has(trial.id);
    
    const handleSendNotification = async () => {
      try {
        // Send trial notification to the parent
        await api.post(`/notifications/send`, {
          user_id: trial.parent_id,
          type: 'trial_reminder',
          title: 'Trial Class Request Update',
          message: `Your trial class request for ${trial.child_name} has been reviewed. Please log in to check the status.`,
          send_email: true
        });
        toast.success('Notification sent to parent');
      } catch (error) {
        // If notification endpoint doesn't exist, show info
        toast.info('Please contact the parent directly using the contact information shown');
      }
    };
    
    return (
    <Card className={`border mb-4 ${selected ? 'border-blue-400 ring-1 ring-blue-200' : 'border-slate-200'}`} data-testid={`trial-card-${trial.id}`}>
      <CardContent className="p-4 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex flex-1 gap-3">
            {selectable && (
              <Checkbox
                checked={selected}
                onCheckedChange={() => toggleSelect(trial.id)}
                className="mt-1"
                data-testid={`trial-select-${trial.id}`}
              />
            )}
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-bold text-lg">
                  {trial.child_name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-slate-900">{trial.child_name}</h3>
                <p className="text-sm text-slate-500">
                  Age: <span className="font-medium text-slate-700">{trial.age} years</span> • 
                  Skill: <span className="font-medium text-slate-700 capitalize">{trial.skill_level}</span>
                </p>
              </div>
            </div>

            {/* Parent Contact Section - Enhanced for Guest vs Registered */}
            <div className={`p-4 rounded-lg ${isGuest ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}>
              {isGuest && (
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-700">
                    Guest Registration - No Account Created
                  </span>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-slate-400" />
                    {isGuest ? (
                      <span className="text-slate-700 font-medium">{trial.parent_name}</span>
                    ) : (
                      <Link 
                        to={`/admin/parents?search=${encodeURIComponent(trial.parent_email || trial.parent_name)}`}
                        className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                        data-testid={`view-parent-${trial.id}`}
                      >
                        {trial.parent_name}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-slate-400" />
                    <a href={`mailto:${trial.parent_email}`} className="text-blue-600 hover:underline">
                      {trial.parent_email}
                    </a>
                  </div>
                  {trial.parent_phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-slate-400" />
                      <a href={`tel:${trial.parent_phone}`} className="text-blue-600 hover:underline">
                        {trial.parent_phone}
                      </a>
                    </div>
                  )}
                </div>
                
                {/* Contact Actions */}
                <div className="flex flex-col gap-2 justify-center">
                  {isGuest ? (
                    <div className="text-xs text-amber-700 bg-amber-100 p-2 rounded">
                      <strong>Action Required:</strong> Contact this parent via email or phone to schedule their trial and encourage account creation.
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      onClick={handleSendNotification}
                      data-testid={`notify-parent-${trial.id}`}
                    >
                      <Bell className="h-4 w-4 mr-1" />
                      Send Notification
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="text-sm">
                <span className="text-slate-500">Preferred Days:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {trial.preferred_days?.map(day => (
                    <Badge key={day} variant="secondary" className="bg-blue-100 text-blue-700">
                      {day}
                    </Badge>
                  ))}
                </div>
              </div>
              {trial.focus_areas?.length > 0 && (
                <div className="text-sm">
                  <span className="text-slate-500">Focus Areas:</span>
                  <span className="ml-2 text-slate-700">{trial.focus_areas.join(', ')}</span>
                </div>
              )}
            </div>

            {trial.cricket_experience && (
              <div className="text-sm bg-slate-50 p-3 rounded-lg">
                <span className="text-slate-500 font-medium">Experience:</span>
                <p className="text-slate-700 mt-1">{trial.cricket_experience}</p>
              </div>
            )}

            {trial.additional_notes && (
              <div className="text-sm">
                <span className="text-slate-500">Notes:</span>
                <p className="text-slate-700">{trial.additional_notes}</p>
              </div>
            )}

            {trial.assigned_class_name && (
              <div className="flex items-center gap-2 text-sm bg-blue-50 p-3 rounded-lg">
                <Calendar className="h-4 w-4 text-blue-600" />
                <span className="text-blue-700">
                  Assigned to: <span className="font-medium">{trial.assigned_class_name}</span>
                  {trial.trial_date && ` on ${trial.trial_date}`}
                </span>
              </div>
            )}
            
            {trial.source === 'landing_page' && (
              <Badge variant="outline" className="text-xs">
                Via Landing Page
              </Badge>
            )}
          </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
              trial.status === 'pending' ? 'bg-amber-100 text-amber-700' :
              trial.status === 'approved' ? 'bg-blue-100 text-blue-700' :
              trial.status === 'completed' ? 'bg-green-100 text-green-700' :
              'bg-slate-100 text-slate-600'
            }`}>
              {trial.status}
            </span>
            
            <span className="text-xs text-slate-400">
              {new Date(trial.created_at).toLocaleDateString()}
            </span>

            {showActions && (
              <div className="flex gap-2 mt-2">
                <Button
                  size="sm"
                  className="bg-blue-500 hover:bg-blue-600"
                  onClick={() => openApprovalDialog(trial)}
                  data-testid={`approve-trial-${trial.id}`}
                >
                  <Check className="h-4 w-4 mr-1" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                  onClick={() => openCancelConfirmation(trial)}
                  data-testid={`cancel-trial-${trial.id}`}
                >
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
              </div>
            )}

            {(trial.status === 'approved' || trial.status === 'completed') && (
              <div className="flex flex-wrap gap-2 mt-2">
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => { setTrialToEnroll(trial); setEnrollDialogOpen(true); }}
                  data-testid={`enroll-trial-${trial.id}`}
                >
                  <GraduationCap className="h-4 w-4 mr-1" />
                  Enroll Player
                </Button>
                {trial.status === 'approved' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-green-600 border-green-200 hover:bg-green-50"
                    onClick={() => handleComplete(trial.id)}
                    data-testid={`complete-trial-${trial.id}`}
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Mark Completed
                  </Button>
                )}
              </div>
            )}

            {trial.status === 'enrolled' && (
              <Badge className="bg-emerald-100 text-emerald-800 mt-2">Enrolled</Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
  };

  if (loading) {
    return (
      <DashboardLayout title="Trial Requests">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Trial Requests">
      <div className="space-y-6" data-testid="trials-page">
        <p className="text-slate-600">Manage trial class requests from parents</p>

        <Tabs value={activeTab} onValueChange={switchTab} className="w-full">
          <TabsList className="grid w-full max-w-xl grid-cols-4">
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending ({pendingTrials.length})
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">
              Approved ({approvedTrials.length})
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              History ({completedTrials.length})
            </TabsTrigger>
            <TabsTrigger value="archived" data-testid="tab-archived">
              Archived ({archivedTrials.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-6">
            {pendingTrials.length === 0 ? (
              <Card className="border border-slate-200">
                <CardContent className="p-8 lg:p-12 text-center">
                  <Star className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No pending trial requests</p>
                </CardContent>
              </Card>
            ) : (
              pendingTrials.map(trial => (
                <TrialCard key={trial.id} trial={trial} showActions />
              ))
            )}
          </TabsContent>

          <TabsContent value="approved" className="mt-6">
            {approvedTrials.length === 0 ? (
              <Card className="border border-slate-200">
                <CardContent className="p-8 lg:p-12 text-center">
                  <Star className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No approved trials scheduled</p>
                </CardContent>
              </Card>
            ) : (
              <>
                <BulkBar list={approvedTrials} />
                {approvedTrials.map(trial => (
                  <TrialCard key={trial.id} trial={trial} selectable />
                ))}
              </>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-6">
            {completedTrials.length === 0 ? (
              <Card className="border border-slate-200">
                <CardContent className="p-8 lg:p-12 text-center">
                  <Star className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No trial history yet</p>
                </CardContent>
              </Card>
            ) : (
              completedTrials.map(trial => (
                <TrialCard key={trial.id} trial={trial} />
              ))
            )}
          </TabsContent>

          <TabsContent value="archived" className="mt-6">
            {archivedTrials.length === 0 ? (
              <Card className="border border-slate-200">
                <CardContent className="p-8 lg:p-12 text-center">
                  <Archive className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No archived trial requests</p>
                </CardContent>
              </Card>
            ) : (
              <>
                <BulkBar list={archivedTrials} />
                {archivedTrials.map(trial => (
                  <TrialCard key={trial.id} trial={trial} selectable />
                ))}
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* Approval Dialog */}
        <Dialog open={approvalDialog} onOpenChange={setApprovalDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Approve Trial Request</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{selectedTrial?.child_name}</p>
                <p className="text-sm text-slate-500">
                  Age: {selectedTrial?.age} • {selectedTrial?.skill_level} • 
                  Preferred: {selectedTrial?.preferred_days?.join(', ') || 'Not specified'}
                </p>
              </div>

              {/* Program Selection */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-blue-500" />
                  Select Program *
                </Label>
                <Select
                  value={approvalData.program_id}
                  onValueChange={handleProgramChange}
                >
                  <SelectTrigger data-testid="trial-program-select">
                    <SelectValue placeholder="Select a program" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4}>
                    {programs.filter(p => p.is_active !== false).map((program) => (
                      <SelectItem key={program.id} value={program.id}>
                        {program.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Class Selection */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-green-500" />
                  Assign to Class *
                </Label>
                <Select
                  value={approvalData.class_id}
                  onValueChange={(value) => setApprovalData({ ...approvalData, class_id: value })}
                  disabled={!approvalData.program_id}
                >
                  <SelectTrigger data-testid="trial-class-select">
                    <SelectValue placeholder={approvalData.program_id ? "Select a class" : "Select program first"} />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4}>
                    {getFilteredClasses().length === 0 ? (
                      <div className="px-2 py-3 text-center text-sm text-slate-500">
                        No classes available for this program
                      </div>
                    ) : (
                      getFilteredClasses().map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} - {c.schedule_day} {c.schedule_time}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Trial Date *</Label>
                <Input
                  type="date"
                  value={approvalData.trial_date}
                  onChange={(e) => setApprovalData({ ...approvalData, trial_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="trial-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Admin Notes (Optional)</Label>
                <Input
                  value={approvalData.admin_notes}
                  onChange={(e) => setApprovalData({ ...approvalData, admin_notes: e.target.value })}
                  placeholder="Any notes about this trial..."
                  data-testid="trial-notes-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setApprovalDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-blue-500 hover:bg-blue-600"
                  onClick={handleApprove}
                  disabled={!approvalData.program_id || !approvalData.class_id}
                  data-testid="confirm-trial-approval"
                >
                  Approve & Assign
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Cancel Trial Confirmation Dialog */}
        <AlertDialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel Trial Request</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to cancel the trial request for "{trialToCancel?.child_name}"? 
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={cancelling}>Keep Request</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmCancel}
                disabled={cancelling}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-cancel-trial-btn"
              >
                {cancelling ? 'Cancelling...' : 'Cancel Request'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Enroll Player from Trial */}
        <TrialEnrollDialog
          open={enrollDialogOpen}
          onOpenChange={setEnrollDialogOpen}
          trial={trialToEnroll}
          programs={programs}
          onSuccess={() => { fetchData(); setActiveTab('completed'); }}
        />

        {/* Bulk Email Dialog (uses the General Announcement template) */}
        <Dialog open={bulkEmailOpen} onOpenChange={(o) => !bulkBusy && setBulkEmailOpen(o)}>
          <DialogContent className="max-w-lg" data-testid="trial-bulk-email-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-blue-600" />
                Email {selectedIds.size} parent{selectedIds.size === 1 ? '' : 's'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-xs text-slate-500">
                Sent using your academy's <strong>General Announcement</strong> email template.
              </p>
              <div className="space-y-2">
                <Label>Subject *</Label>
                <Input
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  placeholder="e.g. Your trial session details"
                  data-testid="trial-email-subject"
                />
              </div>
              <div className="space-y-2">
                <Label>Message *</Label>
                <Textarea
                  rows={6}
                  value={emailForm.message}
                  onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                  placeholder="Write your announcement to the selected parents..."
                  data-testid="trial-email-message"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" disabled={bulkBusy} onClick={() => setBulkEmailOpen(false)}>
                  Cancel
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleBulkEmail}
                  disabled={bulkBusy || !emailForm.subject.trim() || !emailForm.message.trim()}
                  data-testid="trial-email-send-btn"
                >
                  {bulkBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                  Send Email
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Confirmation */}
        <AlertDialog open={bulkDeleteOpen} onOpenChange={(o) => !bulkBusy && setBulkDeleteOpen(o)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {selectedIds.size} trial request{selectedIds.size === 1 ? '' : 's'}?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes the selected trial request{selectedIds.size === 1 ? '' : 's'}. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={bulkBusy}>Keep</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleBulkDelete}
                disabled={bulkBusy}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-bulk-delete-btn"
              >
                {bulkBusy ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default TrialRequests;
